import { useEffect, useRef } from "react";
import { GOOGLE_CLIENT_ID } from "../../../config";

const GSI_SRC = "https://accounts.google.com/gsi/client";

// Carga el script de Google Identity Services una sola vez.
let gsiLoader: Promise<void> | null = null;
const loadGsi = () =>
  (gsiLoader ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiLoader = null;
      reject(new Error("No se pudo cargar Google"));
    };
    document.head.appendChild(script);
  }));

interface Props {
  /** Recibe el ID token (credential) que emite Google. */
  onCredential: (credential: string) => void;
}

/** Botón oficial "Continuar con Google" (Google Identity Services). */
const GoogleSignInButton = ({ onCredential }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    let observer: ResizeObserver | undefined;
    loadGsi()
      .then(() => {
        const google = (window as any).google;
        const el = containerRef.current;
        if (cancelled || !el || !google) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: { credential: string }) =>
            callbackRef.current(response.credential),
        });
        // Google fija el ancho en píxeles al dibujar: se vuelve a dibujar
        // cuando cambia el ancho del contenedor para que coincida con él.
        let lastWidth = 0;
        const render = () => {
          const width = Math.min(400, Math.floor(el.offsetWidth)) || 320;
          if (width === lastWidth) return;
          lastWidth = width;
          google.accounts.id.renderButton(el, {
            theme: "outline",
            size: "large",
            text: "continue_with",
            locale: "es",
            width,
          });
        };
        render();
        // Se dibuja en el siguiente cuadro: hacerlo dentro del callback del
        // observer cambia el tamaño observado y dispara "ResizeObserver loop".
        observer = new ResizeObserver(() => requestAnimationFrame(render));
        observer.observe(el);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, []);

  return <div ref={containerRef} className="google-signin-btn w-100 d-flex justify-content-center" />;
};

export default GoogleSignInButton;
